// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import update from 'immutability-helper';
import classNames from 'classnames/bind';
import Modal from '../../../../elements/Modal/Modal';
import PhoneForm from '../../security/components/PhoneForm/PhoneForm';
import { FormValidator } from '../../../../utils/form';

import style from './Notifications.scss';
import commonStyle from '../../PreferencesViewCommon.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import InputSwitch from '../../../../elements/InputSwitch/InputSwitch';

import type { Settings, User } from '../../../../entities';
import type { Option } from '../../../../elements/Select/Select';
import type { FormValidationResult } from '../../../../utils/form';
import type { LocalValuesState } from '../../../local-values/duck';
import type { HTTPError } from '../../../../error';
import type { ProfileData } from '../../edit-profile/duck';
import Tooltip from '../../../../elements/Tooltip/Tooltip';

type Props = {
  +localValues: LocalValuesState;
  +goToRedirect: (route: string) => void;
  +user?: User;
  +editNotifications: (notificationsSettings: Settings) => void;
  +hasCampaignMailNewsletter: boolean;
  +hasMessageMailNewsletter: boolean;
  +hasMessageMailNotifications: boolean;
  +hasMessagePushNotifications: boolean;
  +hasMessageSmsNotifications: boolean;
  +hasOfferMailNewsletter: boolean;
  +hasOrderUpdateSmsNotifications: boolean;
  // +hasWhatsAppNotifications: boolean;
  +resetAlertState: () => void;
  +editProfile: (profileData: ProfileData) => void;
  +isPutting?: boolean;
  +dataEditSuccessful?: boolean;
  +putError?: HTTPError;
};

type State = {
  hasCampaignMailNewsletter: boolean;
  hasMessageMailNewsletter: boolean;
  hasMessageMailNotifications: boolean;
  hasMessagePushNotifications: boolean;
  hasMessageSmsNotifications: boolean;
  hasOfferMailNewsletter: boolean;
  hasOrderUpdateSmsNotifications: boolean;
  // hasWhatsAppNotifications: boolean;
  country: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  phoneModalVisible: boolean;
};

const messages = defineMessages({
  title: {
    id: 'preferences.notifications.document.title',
  },
  description: {
    id: 'preferences.notifications.document.description',
  }
});

export default class Notifications extends Component<Props, State> {
  formValidatorPhone: FormValidator;

  constructor(props: Props) {
    super();
    this.state = {
      hasCampaignMailNewsletter: props.hasCampaignMailNewsletter,
      hasMessageMailNewsletter: props.hasMessageMailNewsletter,
      hasMessageMailNotifications: props.hasMessageMailNotifications,
      hasMessagePushNotifications: props.hasMessagePushNotifications,
      hasMessageSmsNotifications: props.hasMessageSmsNotifications,
      hasOfferMailNewsletter: props.hasOfferMailNewsletter,
      hasOrderUpdateSmsNotifications: props.hasOrderUpdateSmsNotifications,
      //hasWhatsAppNotifications: props.hasWhatsAppNotifications,
      country: {
        value: '',
      },
      phone: {
        value: '',
      },
      phoneModalVisible: false,
    };
    this.formValidatorPhone = new FormValidator({
      country: {
        required: 'required',
      },
      phone: {
        required: 'required',
      },
    });
  }

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  componentDidUpdate(prevProps: Props) {
    const { dataEditSuccessful, editNotifications } = this.props;
    if (!prevProps.dataEditSuccessful && dataEditSuccessful) {
      editNotifications({
        hasMessageSmsNotifications: !this.state.hasMessageSmsNotifications,
      });
      this.handleClosePhoneModal();
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { checked, name } = e.target;
      const { editNotifications, user, goToRedirect } = this.props;
      this.setState((state: State) => {
        switch (name) {
          case 'hasMessageMailNewsletter':
          case 'hasMessageMailNotifications':
            editNotifications({
              hasMessageMailNewsletter: !state.hasMessageMailNewsletter,
              hasMessageMailNotifications: !state.hasMessageMailNotifications
            });
            return update(state, {
              hasMessageMailNewsletter: { $set: !state.hasMessageMailNewsletter },
              hasMessageMailNotifications: { $set: !state.hasMessageMailNotifications },
            });
          /*case 'whatsapp':
            return update(state, {
              hasWhatsAppNotifications: { $set: !state.hasWhatsAppNotifications },
            });*/
          case 'hasMessageSmsNotifications':
            if (!!user && !user.isPhoneVerified) {
              goToRedirect('/preferences/security');
              return state;
            }
            editNotifications({
              hasMessageSmsNotifications: !state.hasMessageSmsNotifications,
            });
            return update(state, {
              hasMessageSmsNotifications: { $set: !state.hasMessageSmsNotifications },
            });
          case 'hasOrderUpdateSmsNotifications':
            if (!!user && !user.isPhoneVerified) {
              goToRedirect('/preferences/security');
              return state;
            }
            editNotifications({
              hasOrderUpdateSmsNotifications: !state.hasOrderUpdateSmsNotifications,
            });
            return update(state, {
              hasOrderUpdateSmsNotifications: { $set: !state.hasOrderUpdateSmsNotifications },
            });
          default:
            editNotifications({ [name]: checked });
            return update(state, { [name]: { $set: checked } })
        }
      });
    }
  }

  handleOpenPhoneModal = () => {
    this.setState({ phoneModalVisible: true });
  };

  handleClosePhoneModal = () => {
    this.setState({ phoneModalVisible: false });
  };

  handleChangeCountry = (option: Option) => {
    this.setState({
      country: {
        value: `${option.value}`,
      },
    });
  };

  handleChangePhone = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
    }
  };

  handleSubmitPhone = (e: Event) => {
    e.preventDefault();
    const { editProfile } = this.props;
    const phoneValue = this.state.phone.value;
    const countryValue = this.state.country.value;
    const errors = this.formValidatorPhone.validateForm({
      phone: phoneValue,
      country: countryValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
    } else {
      editProfile({ phone: phoneValue, phoneCountry: countryValue });
    }
  };

  render() {
    const {
      hasCampaignMailNewsletter, hasMessageMailNewsletter, hasMessageMailNotifications, hasMessagePushNotifications,
      hasMessageSmsNotifications, hasOfferMailNewsletter, hasOrderUpdateSmsNotifications, phoneModalVisible, country, phone,
    } = this.state;

    const { isPutting, localValues, user } = this.props;

    return (
      <Fragment>
        <Helmet>
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <section className={classNames(boxStyle.box, commonStyle.contentWrap)}>
          <FormattedMessage id="preferences.notifications.title">{
            txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
          }</FormattedMessage>
          <section className={style.switches}>
            <FormattedMessage id="preferences.notifications.mailing">{
              txt => <h3 className={style.subtitle}>{txt}</h3>
            }</FormattedMessage>
            <form>
              <section className={style.dualWrap}>
                <FormattedMessage id="preferences.notifications.mailing.hasMessageMailNewsletter">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasMessageMailNewsletter}
                             name={"hasMessageMailNewsletter"}
                             handleChange={this.handleChange.bind(this)}/>
              </section>
              <section className={style.dualWrap}>
                <FormattedMessage id="preferences.notifications.mailing.hasMessageMailNotifications">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasMessageMailNotifications}
                             name={"hasMessageMailNotifications"}
                             handleChange={this.handleChange.bind(this)}/>
              </section>
              <section className={style.dualWrap}>
                <FormattedMessage id="preferences.notifications.mailing.hasMessagePushNotifications">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasMessagePushNotifications}
                             name={"hasMessagePushNotifications"}
                             handleChange={this.handleChange.bind(this)}/>
              </section>
              <section className={style.dualWrap}>
                <FormattedMessage id="preferences.notifications.mailing.hasCampaignMailNewsletter">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasCampaignMailNewsletter}
                             name={"hasCampaignMailNewsletter"}
                             handleChange={this.handleChange.bind(this)}/>
              </section>
              {/*<section className={classNames(style.dualWrap)}>
              <FormattedMessage id="preferences.security.phone.notification.whatsapp">{
                txt => <label className={style.option}>{txt}</label>
              }</FormattedMessage>
              <InputSwitch value={hasWhatsAppNotifications}
                           name={"whatsapp"}
                           handleChange={this.handleChange.bind(this)}/>
            </section>*/}
              <section className={style.dualWrap}>
                <FormattedMessage id="preferences.notifications.mailing.hasMessageSmsNotifications">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasMessageSmsNotifications}
                             name={"hasMessageSmsNotifications"}
                             handleChange={this.handleChange.bind(this)}/>
                <div className={style.tooltip}>
                  <Tooltip id="notifications" intl="preferences.notifications.mailing.tooltip"/>
                </div>
              </section>
              <section className={classNames(style.dualWrap, style.lastSection)}>
                <FormattedMessage id="preferences.notifications.mailing.hasOrderUpdateSmsNotifications">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasOrderUpdateSmsNotifications}
                             name={"hasOrderUpdateSmsNotifications"}
                             handleChange={this.handleChange.bind(this)}/>
                <div className={style.tooltip}>
                  <Tooltip id="notifications" intl="preferences.notifications.mailing.tooltip"/>
                </div>
              </section>
            </form>
            <FormattedMessage id="preferences.notifications.alerts">{
              txt => <h3 className={style.subtitle}>{txt}</h3>
            }</FormattedMessage>
            <form>
              <section className={classNames(style.dualWrap, style.lastSection)}>
                <FormattedMessage id="preferences.notifications.alerts.hasOfferMailNewsletter">{
                  txt => <label className={style.option}>{txt}</label>
                }</FormattedMessage>
                <InputSwitch value={hasOfferMailNewsletter}
                             name={"hasOfferMailNewsletter"}
                             handleChange={this.handleChange.bind(this)}/>
              </section>
            </form>


          </section>
        </section>
        <Modal open={phoneModalVisible}
               handleClose={this.handleClosePhoneModal}>
          <section className={style.phoneModal}>
            <FormattedMessage id="preferences.security.phone.modal.title">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </section>
          <PhoneForm handleChangeCountry={this.handleChangeCountry}
                     handleChange={this.handleChangePhone}
                     handleFocus={this.handleFocus}
                     handleSubmitPhone={this.handleSubmitPhone}
                     isPostingPhone={isPutting}
                     country={country}
                     phone={(phone: any)}
                     color="light-blue"
                     localValues={localValues}
                     showAcceptSMS={false}/>
        </Modal>
      </Fragment>
    )
  }
}

Notifications.propTypes = {
  localValues: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  user: PropTypes.object,
  editNotifications: PropTypes.func.isRequired,
  hasCampaignMailNewsletter: PropTypes.bool,
  hasMessageMailNewsletter: PropTypes.bool,
  hasMessageMailNotifications: PropTypes.bool,
  hasMessagePushNotifications: PropTypes.bool,
  hasMessageSmsNotifications: PropTypes.bool,
  hasOfferMailNewsletter: PropTypes.bool,
  hasOrderUpdateSmsNotifications: PropTypes.bool,
  // hasWhatsAppNotifications: PropTypes.bool,
  resetAlertState: PropTypes.func.isRequired,
  editProfile: PropTypes.func.isRequired,
  isPutting: PropTypes.bool,
  dataEditSuccessful: PropTypes.bool,
  putError: PropTypes.bool,
};

Notifications.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/notifications/components/Notifications.jsx